/**
 * Where the active language lives in the browser.
 *
 * Four layers, in order of authority (docs/I18N.md):
 *
 *   1. `User.locale`      the database row — the authority once signed in
 *   2. `pd.locale`        a localStorage mirror, so the first paint is already right
 *   3. `Accept-Language`  the browser's own preference, via `navigator.languages`
 *   4. `en`               the fallback
 *
 * Layers 2–4 resolve synchronously at module load, before React renders. Layer 1 arrives with
 * the session and overwrites the rest through `adoptSessionLocale`, which `AuthProvider`
 * already calls (as `setActiveLocale`, from `lib/format.ts`).
 *
 * This module is deliberately free of React and of the auth context: the marketing footer and
 * the sign-in card carry the switcher too, and neither sits inside a session.
 */

import type { Locale } from './shared'
import { DEFAULT_LOCALE, HTML_LANG, normalizeLocale, pickLocale } from './shared'

/** The localStorage mirror. Named in docs/I18N.md — do not rename it. */
export const LOCALE_STORAGE_KEY = 'pd.locale'

const canUseDom = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

const readStored = (): string | null => {
  if (!canUseDom()) return null
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    // Private mode or a blocked origin: the choice still applies, it just does not persist.
    return null
  }
}

const writeStored = (locale: Locale): void => {
  if (!canUseDom()) return
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Same as above — never let storage take the interface down.
  }
}

/** What the browser says it prefers. Stands in for `Accept-Language` on the client. */
const browserLocales = (): readonly string[] => {
  if (typeof navigator === 'undefined') return []
  // `navigator.languages` is typed as a non-optional `readonly string[]`, so `Array.isArray`
  // — a guard that is genuinely useful here, since an old engine may not expose it at all —
  // narrows to `any[]` and loses the element type. Keeping it `unknown` and reading the
  // elements with a real check gives back `string[]` without asserting anything.
  const languages: unknown = navigator.languages
  const tags = Array.isArray(languages)
    ? languages.filter((tag): tag is string => typeof tag === 'string')
    : []
  if (tags.length > 0) return tags
  return typeof navigator.language === 'string' ? [navigator.language] : []
}

const resolveInitialLocale = (): Locale =>
  pickLocale([readStored(), ...browserLocales()]) ?? DEFAULT_LOCALE

let current: Locale = resolveInitialLocale()

const listeners = new Set<() => void>()

const notify = (): void => {
  for (const listener of listeners) listener()
}

/** `<html lang>` follows the active locale on every change — set once at load, then per change. */
const applyDocumentLang = (locale: Locale): void => {
  if (!canUseDom()) return
  document.documentElement.lang = HTML_LANG[locale]
}

applyDocumentLang(current)

export const getLocale = (): Locale => current

export const subscribeToLocale = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Applies a locale locally: state, the `pd.locale` mirror, `<html lang>`, re-render.
 * Does not touch the account — `changeLocale` is the one that does.
 */
export const applyLocale = (locale: Locale): void => {
  writeStored(locale)
  applyDocumentLang(locale)
  if (locale === current) return
  current = locale
  notify()
}

/**
 * The database row won. Called with `User.locale` the moment a session resolves, and again
 * after a reload — an unrecognised value (a legacy `es`, an empty column) leaves the current
 * choice alone rather than dropping the interface back to English.
 */
export const adoptSessionLocale = (value: string | null | undefined): void => {
  const locale = normalizeLocale(value)
  if (locale !== null) applyLocale(locale)
}

/* The third layer — writing `User.locale` through `PATCH /api/v1/users/me` — lives in
   `index.tsx` as `changeLocale`, so this module (and `format.ts` through it) never depends on
   the HTTP client. */
